/**
 * Data transformation utilities for DataCloud
 * Handles data sampling, horizontal spread, and number formatting
 */
import { DataPoint } from '../types/dataCloud.types';

/**
 * Apply horizontal spread to points when X values are too similar
 * This improves chart visualization for vertical line data
 * @param points Array of data points
 * @param spreadFactor Factor to spread identical X values
 * @returns Transformed data points with spread applied
 */
export const applyHorizontalSpread = (
  points: DataPoint[],
  spreadFactor: number = 5
): DataPoint[] => {
  if (points.length === 0) return points;

  // Count unique X values (rounded to 2 decimal places)
  const uniqueX = new Set(points.map(p => Math.round(p.x * 100) / 100));

  // If there are very few unique X values, apply spread
  if (uniqueX.size <= 5) {
    return points.map((p, i) => ({ ...p, x: p.x + i * spreadFactor }));
  }

  return points;
};

/**
 * German locale number formatter for chart axis
 * @param val Number to format
 * @returns Formatted string in German locale
 */
export const germanNumberFormatter = (val: number): string =>
  Number(val).toLocaleString('de-DE', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    useGrouping: true
  });

/**
 * Process regression response data into chart-ready format
 * Processes ALL data points without sampling
 * @param xValues Array of X values
 * @param yValues Array of Y values
 * @param predictedY Optional predicted Y values
 * @param upperBound Optional upper bound values
 * @param lowerBound Optional lower bound values
 * @returns Processed DataPoint array
 */
export const processRegressionData = (
  xValues: number[],
  yValues: number[],
  predictedY?: number[],
  upperBound?: number[],
  lowerBound?: number[]
): DataPoint[] => {
  const chartPoints: DataPoint[] = [];

  // Process ALL data points without sampling
  for (let i = 0; i < xValues.length; i++) {
    const point: DataPoint = {
      x: xValues[i],
      y: yValues[i],
      predicted: predictedY?.[i],
      upperBound: upperBound?.[i],
      lowerBound: lowerBound?.[i],
      regressionValue: predictedY?.[i]
    };
    chartPoints.push(point);
  }

  // Sort by X value
  chartPoints.sort((a, b) => a.x - b.x);

  // Apply horizontal spread once (handles the "all X nearly identical" case).
  return applyHorizontalSpread(chartPoints);
};

/**
 * Validate and normalize data points for chart rendering
 * Ensures all required fields have valid values
 * @param points Array of data points
 * @returns Validated data points
 */
export const validateChartPoints = (points: DataPoint[]): DataPoint[] => {
  return points
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
    .map(point => ({
      x: point.x,
      y: point.y,
      predicted: point.predicted,
      upperBound:
        point.upperBound === undefined || isNaN(point.upperBound)
          ? point.y
          : point.upperBound,
      lowerBound:
        point.lowerBound === undefined || isNaN(point.lowerBound)
          ? point.y
          : point.lowerBound,
      regressionValue:
        point.regressionValue === undefined ||
        point.regressionValue === null ||
        point.regressionValue < 0 ||
        isNaN(point.regressionValue)
          ? null
          : point.regressionValue
    }));
};
